// Configuration de la navigation — regroupée par MODULE FONCTIONNEL, chaque
// module ayant sa couleur, et ADAPTÉE AU TYPE D'UTILISATEUR (persona) :
//
//   'shopify'  (marchand — boutique Shopify connectée, détecté par
//              /api/onboarding/journey) : un module « Boutique Shopify »
//              regroupe tout ce qui touche à la boutique (stores, pubs
//              produits, tracking des ventes, catalogue) juste sous la vue
//              d'ensemble — le marchand pense « ma boutique », pas « quel
//              sous-outil ».
//   'default'  (advertiser) : vue historique orientée campagnes.
//
// Décisions produit transverses :
//   - Chat Alex vit dans « Vue d'ensemble » pour TOUS : c'est la porte
//     d'entrée de l'audit ads / stratégie, pas un sous-outil de création.
//   - Source unique pour la sidebar desktop et le menu mobile.
//
// `accent` : classe de texte/hover du module. `active` : style du lien actif.
// `dot` : pastille de couleur du module. Palette Tailwind (pas de build
// dynamique de classes : toutes sont écrites en toutes lettres pour le purge).

import {
  faChartLine, faBullseye, faLayerGroup, faPlus, faRocket, faSliders,
  faFlaskVial, faClapperboard, faWandMagicSparkles, faRoute, faLink,
  faStore, faBolt, faUserCircle, faCrosshairs, faBoxesStacked, faBrain,
  faCommentDots, faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { faShopify } from "@fortawesome/free-brands-svg-icons";
import { faBell } from "@fortawesome/free-regular-svg-icons";

// ── Briques communes ─────────────────────────────────────────────
const MODULE_OVERVIEW = {
  key: "overview",
  label: "Vue d'ensemble",
  color: "slate",
  dot: "bg-slate-400",
  accent: "text-slate-600",
  activeBg: "bg-slate-100 text-slate-900",
  hover: "hover:bg-slate-50",
  links: [
    { to: "/dashboard/statistics", label: "Overview", icon: faChartLine },
    // Audit ads, stratégie, plan média — accessible dès la vue d'ensemble.
    { to: "/dashboard/alex", label: "Chat Alex · Audit", icon: faCommentDots },
    // Toutes les conversations passées avec Alex (allers-retours complets),
    // triées chronologiquement — un clic reprend la conversation dans Alex.
    { to: "/dashboard/chat-history", label: "Chat History", icon: faClockRotateLeft },
  ],
};

const MODULE_CONNECTORS = {
  key: "connectors",
  label: "Connecteurs",
  color: "violet",
  dot: "bg-violet-500",
  accent: "text-violet-600",
  activeBg: "bg-violet-600 text-white",
  hover: "hover:bg-violet-50",
  links: [
    { to: "/dashboard/connected-platforms", label: "Plateformes", icon: faLink },
    { to: "/dashboard/skills", label: "Skills IA (Alex)", icon: faBrain },
  ],
};

const MODULE_STRATEGY = {
  key: "strategy",
  label: "Stratégie",
  color: "amber",
  dot: "bg-amber-500",
  accent: "text-amber-600",
  activeBg: "bg-amber-500 text-white",
  hover: "hover:bg-amber-50",
  links: [
    { to: "/dashboard/strategy", label: "Plan média & lancement", icon: faRocket },
    { to: "/dashboard/experiments", label: "A/B Testing", icon: faFlaskVial },
  ],
};

const MODULE_CINEMA = {
  key: "cinema",
  label: "Cinema Studio",
  color: "fuchsia",
  dot: "bg-fuchsia-500",
  accent: "text-fuchsia-600",
  activeBg: "bg-fuchsia-600 text-white",
  hover: "hover:bg-fuchsia-50",
  links: [
    { to: "/dashboard/cinema-studio", label: "Cinema Studio", icon: faClapperboard },
    { to: "/dashboard/creative-library", label: "Bibliothèque créas", icon: faLayerGroup },
    { to: "/dashboard/creative-studio", label: "Studio créatif", icon: faWandMagicSparkles },
    { to: "/dashboard/journey/ads", label: "Parcours guidés", icon: faRoute, match: ["/dashboard/journey"] },
  ],
};

const accountModule = (persona) => ({
  key: "account",
  label: "Compte",
  color: "slate",
  dot: "bg-slate-400",
  accent: "text-slate-600",
  activeBg: "bg-slate-800 text-white",
  hover: "hover:bg-slate-50",
  links: [
    // Persona shopify : Stores vit dans « Boutique Shopify », pas ici.
    ...(persona === "shopify" ? [] : [{ to: "/dashboard/stores", label: "Stores", icon: faStore }]),
    { to: "/dashboard/credits", label: "Crédits & Paiement", icon: faBolt },
    { to: "/dashboard/notifications", label: "Notifications", icon: faBell, badge: "unread" },
    { to: "/dashboard/profile", label: "Profil", icon: faUserCircle },
  ],
});

// ── Module TRACKING commun aux deux personas ─────────────────────
// Ordre volontaire : la CONFIGURATION d'abord (c'est le point d'entrée du
// wizard pixels/CAPI), puis la lecture (ventes & attribution), puis le
// Catalogue Meta (flux produits qui alimente le tracking Meta).
const MODULE_TRACKING = {
  key: "tracking",
  label: "Tracking",
  color: "emerald",
  dot: "bg-emerald-500",
  accent: "text-emerald-600",
  activeBg: "bg-emerald-600 text-white",
  hover: "hover:bg-emerald-50",
  links: [
    { to: "/dashboard/tracking-setup", label: "Configurer le tracking", icon: faSliders },
    { to: "/dashboard/tracking", label: "Ventes & attribution", icon: faBullseye },
    { to: "/dashboard/meta-catalog", label: "Catalogue Meta", icon: faBoxesStacked },
  ],
};

// ── Vue MARCHAND SHOPIFY ─────────────────────────────────────────
const SHOPIFY_MODULES = [
  MODULE_OVERVIEW,
  {
    // La BOUTIQUE : boutique + pubs produits. Le tracking a son module dédié.
    key: "shopify",
    label: "Boutique Shopify",
    color: "emerald",
    dot: "bg-emerald-500",
    accent: "text-emerald-600",
    activeBg: "bg-emerald-600 text-white",
    hover: "hover:bg-emerald-50",
    links: [
      { to: "/dashboard/stores", label: "Ma boutique", icon: faStore },
      { to: "/dashboard/shopify-ads", label: "Pubs produits", icon: faShopify },
    ],
  },
  MODULE_TRACKING,
  {
    key: "ads",
    label: "Ads Management",
    color: "blue",
    dot: "bg-blue-500",
    accent: "text-blue-600",
    activeBg: "bg-blue-600 text-white",
    hover: "hover:bg-blue-50",
    links: [
      { to: "/dashboard/campaigns", label: "Campagnes", icon: faLayerGroup },
      { to: "/dashboard/create-ad-ai", label: "AI Launcher", icon: faWandMagicSparkles },
      { to: "/dashboard/create-ad", label: "Créer une pub", icon: faPlus },
      { to: "/dashboard/pixels", label: "Pixels", icon: faCrosshairs },
      { to: "/dashboard/ads-ruleset", label: "Règles auto", icon: faSliders },
    ],
  },
  MODULE_CONNECTORS,
  MODULE_STRATEGY,
  MODULE_CINEMA,
  accountModule("shopify"),
];

// ── Vue ADVERTISER (défaut) ──────────────────────────────────────
const DEFAULT_MODULES = [
  MODULE_OVERVIEW,
  {
    key: "ads",
    label: "Ads Management",
    color: "blue",
    dot: "bg-blue-500",
    accent: "text-blue-600",
    activeBg: "bg-blue-600 text-white",
    hover: "hover:bg-blue-50",
    links: [
      { to: "/dashboard/campaigns", label: "Campagnes", icon: faLayerGroup },
      { to: "/dashboard/pixels", label: "Pixels", icon: faCrosshairs },
      { to: "/dashboard/create-ad-ai", label: "AI Launcher", icon: faWandMagicSparkles },
      { to: "/dashboard/create-ad", label: "Créer une pub", icon: faPlus },
      { to: "/dashboard/shopify-ads", label: "Shopify Ads", icon: faShopify },
      { to: "/dashboard/ads-ruleset", label: "Règles auto", icon: faSliders },
    ],
  },
  MODULE_TRACKING,
  MODULE_CONNECTORS,
  MODULE_STRATEGY,
  MODULE_CINEMA,
  accountModule("default"),
];

// Navigation adaptée au persona ('shopify' | 'default').
export function getNavModules(persona = "default") {
  return persona === "shopify" ? SHOPIFY_MODULES : DEFAULT_MODULES;
}

// Rétrocompatibilité : consommateurs historiques sans persona.
export const NAV_MODULES = DEFAULT_MODULES;

// Le chemin courant correspond-il à ce lien ? (gère les sous-routes via `match`)
export function isLinkActive(link, pathname) {
  if (pathname === link.to) return true;
  return (link.match || []).some((m) => pathname === m || pathname.startsWith(m));
}
